import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Users, CheckCircle2, XCircle, Clock, Trash2, Car, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_UI = {
  pending: { label: "Pending", cls: "text-[#F2B705] border-[#F2B705]/40 bg-[#F2B705]/10" },
  approved: { label: "Approved", cls: "text-green-400 border-green-500/40 bg-green-500/10" },
  rejected: { label: "Rejected", cls: "text-red-400 border-red-500/40 bg-red-500/10" },
};

function DocImage({ path, label, token }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!path) return;
    let active = true;
    let objUrl;
    axios.get(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then((res) => { if (active) { objUrl = URL.createObjectURL(res.data); setUrl(objUrl); } })
      .catch(() => active && setErr(true));
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path, token]);
  if (!path) return <div className="h-20 w-28 rounded-md border border-white/10 bg-black flex items-center justify-center text-white/25 text-xs">no {label}</div>;
  return (
    <a href={url || undefined} target="_blank" rel="noreferrer" className="block w-28">
      <div className="h-20 w-28 rounded-md border border-white/15 overflow-hidden bg-black flex items-center justify-center">
        {err ? <span className="text-white/30 text-xs">unavailable</span>
          : url ? <img src={url} alt={label} className="w-full h-full object-cover" />
          : <Loader2 className="animate-spin text-white/40" size={16} />}
      </div>
      <span className="text-[11px] text-white/45 mt-1 block text-center">{label}</span>
    </a>
  );
}

function StatBox({ label, value, tone }) {
  const tones = { pending: "text-[#F2B705]", approved: "text-green-400", rejected: "text-red-400" };
  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
      <div className="text-white/45 text-xs uppercase tracking-widest">{label}</div>
      <div className={`font-display font-black text-3xl mt-1.5 ${tones[tone] || "text-white"}`}>{value}</div>
    </div>
  );
}

export default function DriversManager({ token, onAuthError }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const fetchData = () => axios.get(`${API}/drivers${filter ? `?status=${filter}` : ""}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setData(r.data))
    .catch((e) => { if (e?.response?.status === 401) onAuthError?.(); })
    .finally(() => setLoading(false));

  useEffect(() => { setLoading(true); fetchData(); /* eslint-disable-next-line */ }, [token, filter]);

  const setStatus = async (id, status) => {
    let reason = "";
    if (status === "rejected") {
      reason = window.prompt("Reason for rejection (optional, the renter will see this):") || "";
    }
    try {
      await axios.post(`${API}/drivers/${id}/status?status=${status}&reason=${encodeURIComponent(reason)}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Driver ${status}. Renter notified by email.`);
      fetchData();
    } catch {
      toast.error("Could not update driver.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this driver submission?")) return;
    try {
      await axios.delete(`${API}/drivers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted.");
      fetchData();
    } catch {
      toast.error("Could not delete.");
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  if (!data) return <p className="text-white/40">Could not load drivers.</p>;

  return (
    <div data-testid="drivers-dashboard" className="space-y-6">
      <div>
        <h2 className="font-display font-black text-2xl flex items-center gap-2"><Users className="text-[#F2B705]" size={22} /> Authorized Drivers</h2>
        <p className="text-white/45 text-sm mt-1">Review and approve additional drivers submitted by renters.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatBox label="Pending" value={data.stats.pending} tone="pending" />
        <StatBox label="Approved" value={data.stats.approved} tone="approved" />
        <StatBox label="Rejected" value={data.stats.rejected} tone="rejected" />
      </div>

      <div className="flex gap-2">
        {[["", "All"], ["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"]].map(([v, l]) => (
          <button key={v} data-testid={`drivers-filter-${v || "all"}`} onClick={() => setFilter(v)}
            className={`text-sm px-4 py-1.5 rounded-md border transition-colors ${filter === v ? "bg-[#F2B705] text-black border-[#F2B705] font-semibold" : "border-white/15 text-white/60 hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>

      {data.drivers.length === 0 ? (
        <div data-testid="drivers-empty" className="text-center py-16 border border-white/10 rounded-xl bg-[#0A0A0A] text-white/50">
          No driver submissions{filter ? ` with status "${filter}"` : " yet"}.
        </div>
      ) : (
        <div className="space-y-3">
          {data.drivers.map((d) => {
            const ui = STATUS_UI[d.status] || STATUS_UI.pending;
            return (
              <div key={d.id} data-testid={`driver-row-${d.id}`} className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-semibold text-lg">
                      {d.name}
                      <span className={`text-xs px-2.5 py-1 rounded-full border ${ui.cls}`}>{ui.label}</span>
                    </div>
                    <div className="text-white/45 text-xs mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="flex items-center gap-1"><Car size={12} /> {d.vehicle}</span>
                      <span>Renter: {d.renter_name}</span>
                      {d.renter_phone && <a href={`tel:${d.renter_phone}`} className="flex items-center gap-1 text-[#F2B705]"><Phone size={11} /> {d.renter_phone}</a>}
                      {d.renter_email && <a href={`mailto:${d.renter_email}`} className="flex items-center gap-1 text-[#F2B705]"><Mail size={11} /> {d.renter_email}</a>}
                    </div>
                    <div className="text-white/55 text-sm mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
                      {d.date_of_birth && <span>DOB: {d.date_of_birth}</span>}
                      {d.dl_number && <span>DL #: {d.dl_number}</span>}
                      {d.dl_state && <span>State: {d.dl_state}</span>}
                      {d.dl_expiration && <span>Expires: {d.dl_expiration}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <DocImage path={d.license_photo_path} label="License" token={token} />
                    <DocImage path={d.selfie_path} label="Selfie" token={token} />
                  </div>
                </div>

                {d.status === "rejected" && d.reject_reason && (
                  <p className="text-red-400/80 text-xs mt-2">Reason: {d.reject_reason}</p>
                )}

                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/5">
                  {d.status !== "approved" && (
                    <button data-testid={`driver-approve-${d.id}`} onClick={() => setStatus(d.id, "approved")}
                      className="text-xs flex items-center gap-1.5 border border-green-500/40 text-green-400 px-3 py-1.5 rounded hover:bg-green-500 hover:text-white transition-colors">
                      <CheckCircle2 size={13} /> Approve
                    </button>
                  )}
                  {d.status !== "rejected" && (
                    <button data-testid={`driver-reject-${d.id}`} onClick={() => setStatus(d.id, "rejected")}
                      className="text-xs flex items-center gap-1.5 border border-red-500/40 text-red-400 px-3 py-1.5 rounded hover:bg-red-500 hover:text-white transition-colors">
                      <XCircle size={13} /> Reject
                    </button>
                  )}
                  {d.status !== "pending" && (
                    <button data-testid={`driver-pending-${d.id}`} onClick={() => setStatus(d.id, "pending")}
                      className="text-xs flex items-center gap-1.5 border border-white/15 text-white/60 px-3 py-1.5 rounded hover:text-white transition-colors">
                      <Clock size={13} /> Mark Pending
                    </button>
                  )}
                  <button data-testid={`driver-delete-${d.id}`} onClick={() => remove(d.id)}
                    className="text-xs flex items-center gap-1.5 border border-white/15 text-white/50 px-3 py-1.5 rounded hover:text-red-400 transition-colors ml-auto">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
